import type { Router } from "vue-router"
import type { QNotifyCreateOptions, QNotifyAction } from "quasar"
import { Notify } from "quasar"
import { ROUTE_NAME_ACCOUNTS } from "src/router/routes"
import { i18n } from "boot/i18n"
import type { InjectionKey } from "vue"

const { t } = i18n.global

export default class NotificationService {
    private readonly router: Router
    private DEFAULT_DISPLAY_TIME = 5000

    /**
     * Do not instantiate this class!
     *
     * Instead, use useNotificationService()
     */
    public constructor(router: Router) {
        this.router = router
    }

    notify(notificationOptions: QNotifyCreateOptions | string, origin?: string[]): void {
        // The path includes /
        if (!origin || origin.includes(this.router.currentRoute.value.path.slice(1))) {
            if (typeof notificationOptions === "object" && notificationOptions.timeout === undefined) {
                notificationOptions.timeout = this.DEFAULT_DISPLAY_TIME
            }

            Notify.create(notificationOptions)
        }
    }

    notifySoonExpiration(validUntil: number, hasPendingPayment: boolean): void {
        let message = "Your subscription expires soon."
        const actions: QNotifyAction[] = [
            {
                label: "Dismiss",
                color: "white",
                handler: (): void => {
                    /* ... */
                },
            },
        ]
        if (hasPendingPayment) {
            message += " Please complete the payment to activate your account and unlock the full features."
        } else {
            actions.unshift({
                label: "Upgrade",
                handler: (): void => {
                    this.router.push({ name: ROUTE_NAME_ACCOUNTS })
                },
            })
        }
        this.notify({
            message,
            caption: "Expires: " + new Date(validUntil * 1000).toDateString(),
            actions,
            type: "warning",
            timeout: 0,
            position: "top-right",
            icon: "spi-triangle-exclamation",
        })
    }

    notifyPendingPayment(): void {
        const message = t("SubscriptionOverviewComponent.subscription.awaitPayment")
        const actions: QNotifyAction[] = [
            {
                label: "Dismiss",
                color: "white",
                handler: (): void => {
                    /* ... */
                },
            },
        ]
        this.notify({
            message,
            actions,
            type: "warning",
            timeout: 0,
            position: "top-right",
            icon: "spi-triangle-exclamation",
        })
    }

    notifySubscriptionExpired(): void {
        this.notify({
            message: "Yours subscription has expired.",
            caption: "To continue using Stockpulse, please purchase a new subscription.",
            actions: [
                {
                    label: "Ok",
                    handler: (): void => {
                        /* ... */
                    },
                },
            ],
            type: "warning",
            timeout: 0,
            position: "top-right",
            icon: "spi-triangle-exclamation",
        })
    }
}

export const NotificationServiceKey: InjectionKey<NotificationService> = Symbol("NotificationService")
